(() => {
  'use strict';

  const OUTPUT_HEIGHT = 1152;

  function relativeRect(element, posterRect) {
    const rect = element.getBoundingClientRect();
    return {
      x: rect.left - posterRect.left,
      y: rect.top - posterRect.top,
      width: rect.width,
      height: rect.height
    };
  }

  function baseline(element, posterRect) {
    const marker = document.createElement('span');
    marker.style.cssText = 'display:inline-block;width:0;height:0;padding:0;margin:0;vertical-align:baseline;';
    element.appendChild(marker);
    const value = marker.getBoundingClientRect().top - posterRect.top;
    marker.remove();
    return value;
  }

  function graphemes(value) {
    if (typeof Intl.Segmenter === 'function') {
      return Array.from(new Intl.Segmenter('fr', { granularity: 'grapheme' }).segment(value), (part) => part.segment);
    }
    return Array.from(value);
  }

  function fontFromStyle(style) {
    return `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  }

  function trackedWidth(context, value, spacing) {
    const letters = graphemes(value);
    return context.measureText(value).width + Math.max(0, letters.length - 1) * spacing;
  }

  function drawText(context, value, x, y, element, options = {}) {
    const style = getComputedStyle(element);
    const spacing = Number.parseFloat(style.letterSpacing) || 0;
    context.font = fontFromStyle(style);
    context.fillStyle = style.color;
    context.textBaseline = 'alphabetic';
    const width = trackedWidth(context, value, spacing);
    const start = options.centered ? x - width / 2 : x;
    if ('letterSpacing' in context) {
      context.letterSpacing = `${spacing}px`;
      context.fillText(value, start, y);
      context.letterSpacing = '0px';
      return;
    }
    let cursor = start;
    const letters = graphemes(value);
    letters.forEach((letter, index) => {
      context.fillText(letter, cursor, y);
      cursor += context.measureText(letter).width + (index < letters.length - 1 ? spacing : 0);
    });
  }

  async function loadLogo(source) {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = source.currentSrc || source.src;
    await image.decode();
    return image;
  }

  async function createFile(poster, filename) {
    if (!(poster instanceof HTMLElement) || !poster.isConnected) {
      throw new Error('Aperçu introuvable. Revenez à la relecture de votre affiche.');
    }
    await Promise.all([
      document.fonts.load('700 100px "Barlow Condensed"'),
      document.fonts.load('400 20px "Space Mono"'),
      document.fonts.load('italic 400 20px "Space Mono"'),
      document.fonts.load('400 20px "Glacial Indifference"')
    ]);
    const posterRect = poster.getBoundingClientRect();
    if (posterRect.width < 1 || posterRect.height < 1) {
      throw new Error('Aperçu indisponible. Rechargez la page puis réessayez.');
    }
    const wordLines = Array.from(poster.querySelectorAll('.poster-customizer__poster-word > span'));
    const definition = poster.querySelector('.poster-customizer__poster-definition');
    const name = poster.querySelector('.poster-customizer__poster-name');
    const logo = poster.querySelector('.poster-customizer__poster-brand img');
    if (!wordLines.length || !definition || !name || !logo) {
      throw new Error('L’affiche n’est pas complète. Vérifiez sa personnalisation.');
    }
    const logoImage = await loadLogo(logo);
    const canvas = document.createElement('canvas');
    canvas.height = OUTPUT_HEIGHT;
    canvas.width = Math.round(OUTPUT_HEIGHT * posterRect.width / posterRect.height);
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Votre navigateur ne peut pas préparer l’aperçu.');
    context.scale(canvas.width / posterRect.width, canvas.height / posterRect.height);
    context.fillStyle = getComputedStyle(poster).backgroundColor;
    context.fillRect(0, 0, posterRect.width, posterRect.height);

    wordLines.forEach((line) => {
      const word = Array.from(line.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join('');
      const position = relativeRect(line, posterRect);
      drawText(context, word, position.x, baseline(line, posterRect), line);
      const dot = line.querySelector('.poster-customizer__poster-dot');
      if (dot) {
        const circle = relativeRect(dot, posterRect);
        context.beginPath();
        context.arc(circle.x + circle.width / 2, circle.y + circle.height / 2, circle.width / 2, 0, Math.PI * 2);
        context.fillStyle = getComputedStyle(dot).backgroundColor;
        context.fill();
      }
    });

    const definitionText = Array.from(definition.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join('');
    const definitionPosition = relativeRect(definition, posterRect);
    const definitionBaseline = baseline(definition, posterRect);
    drawText(context, definitionText, definitionPosition.x, definitionBaseline, definition);
    const definitionItalic = definition.querySelector('em');
    if (definitionItalic) {
      drawText(context, definitionItalic.textContent, relativeRect(definitionItalic, posterRect).x, definitionBaseline, definitionItalic);
    }

    const namePosition = relativeRect(name, posterRect);
    drawText(context, name.textContent, namePosition.x + namePosition.width / 2, baseline(name, posterRect), name, { centered: true });
    const logoPosition = relativeRect(logo, posterRect);
    context.drawImage(logoImage, logoPosition.x, logoPosition.y, logoPosition.width, logoPosition.height);

    const blob = await new Promise((resolve, reject) => {
      try {
        canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Aperçu image indisponible.')), 'image/png');
      } catch (error) {
        reject(error);
      }
    });
    return new File([blob], filename, { type: 'image/png' });
  }

  window.LpaPosterEmailPreview = { createFile };
})();
